import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DownloadIcon from '../../../assets/icons/download-cloud-02.svg';
import Button from '../../../components/Button/Button';
import { PATHNAME } from '../../../pathname';
import CustomPagination from '../../../components/Common/CustomPagination/CustomPagination';
import { getAllUnits } from '../../../redux/unitSlice/unitSlice';
import CustomSelectWithFilter from '../../../components/Common/CustomSelect/CustomSelectWithFilter';
import { exportExcel } from '../../../redux/adminUnitReportSlice/adminUnitReportSlice';
import CustomTable from '../../../components/Common/CustomTable/CustomTable';
import { getRegionUnitOtherProjectsDetailsListing } from '../../../redux/otherProjectsSlice/otherProjectsSlice';
import Breadcrumb from '../../../components/BreadCrumb/BreadCrumb';
import dayjs from "dayjs";
import { DatePicker } from 'antd';
import { format } from 'date-fns';

const getCurrentFYRange = () => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;

        const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;

        return {
                fromDate: dayjs(`${fyStartYear}-04-01`),
                toDate: dayjs(`${fyStartYear + 1}-03-01`),
        };
};

const { fromDate, toDate } = getCurrentFYRange();

const AdminOtherProjectsDetails = () => {
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const [currentPage, setCurrentPage] = useState(1);
        const pageSize = 10; // Items per page
        const { RangePicker } = DatePicker;
        const { allUnitsList } = useSelector((state) => state.unit);
        const { otherProjectDetailsListing, otherProjectDetailsListingTotal, loading } = useSelector((state) => state.otherProjects);
        const [searchParams] = useSearchParams();
        const IO_NO = searchParams.get('ioNo'); // automatically decoded
        const REV_NO = searchParams.get('revNo'); // automatically decoded
        const [mnfUnits, setMnfUnits] = useState(null);
        const [selectedDateRange, setSelectedDateRange] = useState([
                fromDate,
                toDate,
        ]);
        const [monthRange, setMonthRange] = useState({
                from_date: `01-04-${fromDate.year()}`,
                to_date: `01-03-${toDate.year()}`,
        });

        const handleMonthRangeChange = (dates) => {
                setSelectedDateRange(dates);
                if (dates && dates.length === 2) {
                        const fromDate = dates[0].toDate();
                        const toDate = dates[1].toDate();

                        setMonthRange({
                                from_date: format(fromDate, '01-MM-yyyy'),
                                to_date: format(toDate, '01-MM-yyyy'),
                        });
                } else {
                        setMonthRange({
                                from_date: null,
                                to_date: null,
                        });
                }
        };

        const unitRegionColumns = [
                {
                        title: 'Units / Region',
                        dataIndex: 'UNIT_NAME',
                        key: 'UNIT_NAME',
                        align: 'center',
                        fixed: 'left',
                        width: 200,
                        render: (text, record) => (
                                <a
                                        className="text-blue-500 underline cursor-pointer text-right"
                                        onClick={() => navigate(`${PATHNAME.ADMIN_OTHER_PROJECTS}/${encodeURIComponent(text)}/final-preview-other-projects?&mnfUnit=${record.MNF_UNIT}&unitType=${record.UNIT_TYPE}`)}>
                                        {text || '--'}
                                </a>
                        ),
                },
                {
                        title: 'Project Name',
                        dataIndex: 'PROJECT_TITLE',
                        key: 'PROJECT_TITLE',
                        align: 'center',
                        fixed: 'left',
                        width: 300,

                        render: (text) => <span className="text-center">{`${text}`}</span>,
                },
                {
                        title: 'Io No',
                        dataIndex: 'IO_NO',
                        key: 'IO_NO',
                        align: 'center',
                        fixed: 'left',
                        width: 200,

                        render: (text) => <span className="text-center">{`${text}`}</span>,
                },

                {
                        title: 'Revenue',
                        dataIndex: 'REVENUE',
                        key: 'REVENUE',
                        align: 'center',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Project Cost',
                        dataIndex: 'PROJECT_COST',
                        key: 'PROJECT_COST',
                        align: 'center',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Other Apportioned Cost',
                        dataIndex: 'APPORTIONED_COST',
                        key: 'APPORTIONED_COST',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Other Operational Income',
                        dataIndex: 'TOTAL_OPERATIONAL_INCOME',
                        key: 'TOTAL_OPERATIONAL_INCOME',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Profit/Loss',
                        dataIndex: 'PROFIT_LOSS',
                        key: 'PROFIT_LOSS',
                        width: 150,

                        render: (text) => {
                                const value = text && Number(text.split(',').join(''));
                                const color = value < 0 ? 'text-red-500' : 'text-green-500';
                                return <div className={`text-right ${color} text-[14px]`}>{text || '--'}</div>;
                        },
                },

                {
                        title: 'Net Billing',
                        dataIndex: 'TOTAL_NET_BILLING',
                        key: 'TOTAL_NET_BILLING',
                        width: 150,

                        render: (text) => {
                                const value = text && Number(text.split(',').join(''));
                                const color = value < 0 ? 'text-red-500' : 'text-green-500';
                                return <div className={`text-right ${color} text-[14px]`}>{text || '--'}</div>;
                        },
                },
                {
                        title: 'Cash Inflow',
                        dataIndex: 'TOTAL_CASH_INFLOW',
                        key: 'TOTAL_CASH_INFLOW',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Cash Outflow',
                        dataIndex: 'TOTAL_CASH_OUTFLOW',
                        key: 'TOTAL_CASH_OUTFLOW',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
                {
                        title: 'Net Cashflow',
                        dataIndex: 'NET_CASH_FLOW',
                        key: 'NET_CASH_FLOW',
                        width: 150,

                        render: (text) => {
                                const value = text && Number(text.split(',').join(''));
                                const color = value < 0 ? 'text-red-500' : 'text-green-500';
                                return <div className={`text-right ${color} text-[14px]`}>{text || '--'}</div>;
                        },
                },
                {
                        title: 'DM%',
                        dataIndex: 'DM_PERCENT',
                        key: 'DM_PERCENT',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '--'}</div>;
                        },
                },
        ];

        useEffect(() => {
                dispatch(
                        getRegionUnitOtherProjectsDetailsListing({
                                page: currentPage,
                                limit: pageSize,
                                IO_NO: IO_NO,
                                REV_NO: REV_NO,
                                mnfUnits: mnfUnits,
                                monthRange: monthRange, // Pass monthRange to action
                        }),
                );
        }, [currentPage, mnfUnits, monthRange]);

        useEffect(() => {
                dispatch(getAllUnits());
        }, []);

        return (
                <div className="pb-14">
                        <div className="px-5 flex flex-col gap-6 pt-8">
                                <Breadcrumb queryParams={`ioNo=${IO_NO}&revNo=${REV_NO}`} />
                                <div className="flex justify-between">
                                        <div className="font-inter font-semibold text-[24px] leading-[32px] tracking-[0%] text-[#101828]">Other Projects</div>
                                </div>
                                <div className="text-gray-500 text-sm flex justify-start">* All figures are in Rupees (in Crores).</div>
                                <div className="flex justify-end gap-3">
                                        <RangePicker
                                                picker="month"
                                                format="MMMM YYYY"
                                                onChange={handleMonthRangeChange}
                                                value={selectedDateRange}
                                                placeholder={['From Month', 'To Month']}
                                                allowClear
                                                className='h-10'
                                        />
                                        <CustomSelectWithFilter
                                                showSearch="true"
                                                data={
                                                        Array.isArray(allUnitsList)
                                                                ? allUnitsList.map((value) => ({
                                                                        label: value?.UNIT_DISPLAY_NAME,
                                                                        value: value?.MNF_UNIT,
                                                                }))
                                                                : []
                                                }
                                                onChange={(value) => {
                                                        setMnfUnits(value);
                                                }}
                                                placeholder="Select Unit/Region"
                                                className="w-[15rem]"
                                        />
                                        <Button
                                                text="Export report"
                                                icon={DownloadIcon}
                                                onClick={() =>
                                                        dispatch(
                                                                exportExcel({
                                                                        key: 'admin_other_projects_details_list',
                                                                        IO_NO: IO_NO,
                                                                        REV_NO: REV_NO,
                                                                }),
                                                        )
                                                }
                                        />
                                </div>
                        </div>
                        <div className="px-5 flex flex-col gap-6 pt-8">
                                <CustomTable
                                        dataSource={otherProjectDetailsListing}
                                        columns={unitRegionColumns}
                                        loading={loading}
                                />
                                <div>
                                        <CustomPagination
                                                totalItems={otherProjectDetailsListingTotal}
                                                pageSize={pageSize}
                                                currentPage={currentPage}
                                                setCurrentPage={setCurrentPage}
                                        />
                                </div>
                        </div>
                </div>
        );
};

export default AdminOtherProjectsDetails;
